import { Rpm_PerfilesEstudios, SysA_AreasAnalisis, SysI_Estudios } from './types.estudios';
import { SysALaboratoriosRemisiones } from './types.remissions';
import { Profile, User } from './types.user';

// Config_General llega en el endpoint público (pre-login). El resto de nodos
// solo llega tras autenticar, vía GetAllConfig, por lo que son opcionales:
// pueden no estar durante el arranque de la sesión.
export interface Config {
  Config_General: Config_General[];
  ComboBox?: TypeCombobox[];
  LabReferencia?: SysALaboratoriosRemisiones[];
  SystemFields?: SystemFields[];
  Estudios?: SysI_Estudios[];
  Perfiles?: Rpm_PerfilesEstudios[];
  Areas?: SysA_AreasAnalisis[];
  AnalitosValoresRef?: SysIAnalitosValoresRef[];
  DefinicionesCampos?: SystemFields[];
  NotasPlantillas?: SysINotasPlantillas[];
  UsuariosAgendas?: User[];
  UsuariosPerfiles?: Profile[];
}

// Payload de GetAllConfig: todo menos lo que ya trae el endpoint público.
export type FullConfig = Omit<Config, 'Config_General'>;

export interface SysINotasPlantillas {
  Pln_Id: number;
  Pln_Abreviatura: string;
  Pln_Titulo: string;
  Pln_Notas: string;
  Pln_Tipo: TypeNotasPlantillas;
  Pln_Estado: boolean;
}

export enum TypeNotasPlantillas {
  Validation = 1,
  Invalidation = 2,
  Internals = 3,
  Templates = 4,
  Tracking = 5,
  Alarms = 6,
  EliminacionOrden = 7,
  Preordenes = 8,
  Rollback = 9,
  Microbiologia = 10,
}

export interface InitialConfig {
  Loading: boolean;
  IsAuthenticated: boolean;
  // Indica si GetAllConfig ya respondió. Las pantallas pueden navegar sin
  // esperarlo, pero las acciones que dependen de las listas deben aguardarlo.
  FullConfigLoaded: boolean;
  Error: string | null;
  Data: Config | null;
  HeightPanels: number;
  IsEditing: boolean;
  LstAnalitosResulTipoListas: SysIAnalitosResulTipoListas[];
  LstAccionesMonitoreo: SysIAccionesMonitoreo[];
  LstUsuarios: User[];
  ModalBarcodePrintersVisible: boolean;
  apiUrl: string | null;
  signalRUrl: string | null;
}

export interface Config_General {
  Cgn_Id: number;
  Cgn_Nombre: string;
  Cgn_Resultado: string;
  Cgn_Descripcion?: string | null;
  Cgn_Label?: string | null;
  Cgn_Control: typeControl;
  Cgn_Orden: number;
  Cgn_Estado: boolean;
  Cng_Grupo: GruposConfigGeneral;
  Cng_ControlListas: any;
  Cng_Requerido: boolean;
  Cgn_EsContrasena?: boolean;
  Cgn_SubGrupo?: string | null;
}

export interface SysIAnalitosResulTipoListas {
  Rtl_Id: number;
  Rtl_An_Id: number;
  Rtl_Resultado: string;
  Rtl_Descripcion: string | null;
  Rtl_Abreviatura: string;
}

export interface SysIAnalitosValoresRef {
  Avr_Id: number;
  Avr_An_Id: number;
  Avr_Sd_Id: number;
  Avr_MinimoHombre: number | null;
  Avr_MaximoHombre: number | null;
  Avr_MinimoMujer: number | null;
  Avr_MaximoMujer: number | null;
  Avr_MinimoEmbarazo: number | null;
  Avr_MaximoEmbarazo: number | null;
  Avr_MinimoAlarma: number | null;
  Avr_MaximoAlarma: number | null;
  Avr_MinimoAbsurdo: number | null;
  Avr_MaximoAbsurdo: number | null;
}

export interface SystemFields {
  Dfc_Id: number;
  Dfc_Tcm_Id: number;
  Dfc_Tbc_Id?: number;
  Dfc_Label: string;
  Dfc_Tabla: string;
  Dfc_Equivalencia: string;
  Dfc_Requerido: boolean;
  Dfc_Editable: boolean;
  Dfc_Minimo: string | null;
  Dfc_Maximo: string | null;
  Dfc_Orden: number;
  Dfc_NroColumnas: number;
  Dfc_Dinamico: boolean;
  Dfc_Mascara: string | null;
  Dfc_Formato: string | null;
  Dfc_Estado: boolean;
  Dfc_Cast: 'UPPER' | 'LOWER';
  Dfc_Dependency: number;
  Dfc_DependencyField: string;
  Dfc_DependencyDefinition: number;
  Dfc_Visible: boolean;
  TipoCampo: TipoCampo;
  TablasComboBox: TablasCombobox;
  TablasComboBoxDependency: TablasCombobox;
}

export interface TipoCampo {
  Tcm_Id: number;
  Tcm_Nombre: string;
  Tcm_Equivalencia: typeControl;
  Tcm_Estado: boolean;
}

export interface TablasCombobox {
  Tbc_Id: number;
  Tbc_NombreTabla: string;
  Tbc_CampoId: string;
  Tbc_CampoVisible: string;
  Tbc_CampoEstado: string;
  Tbc_CampoOrden: string;
}

type typeControl = 'dxAutocomplete' | 'dxCalendar' | 'dxCheckBox' | 'dxColorBox' | 'dxDateBox' | 'dxDropDownBox' | 'dxHtmlEditor' | 'dxLookup' | 'dxNumberBox' | 'dxRadioGroup' | 'dxRangeSlider' | 'dxSelectBox' | 'dxSlider' | 'dxSwitch' | 'dxTagBox' | 'dxTextArea' | 'dxTextBox';

export const Types = [
  { id: 1, name: 'Autocomplete' },
  { id: 2, name: 'Calendar' },
  { id: 3, name: 'CheckBox' },
  { id: 4, name: 'ColorBox' },
  { id: 5, name: 'DateBox' },
  { id: 6, name: 'DropDownBox' },
  { id: 7, name: 'HtmlEditor' },
  { id: 8, name: 'Lookup' },
  { id: 9, name: 'NumberBox' },
  { id: 10, name: 'RadioGroup' },
  { id: 11, name: 'RangeSlider' },
  { id: 12, name: 'SelectBox' },
  { id: 13, name: 'Slider' },
  { id: 14, name: 'Switch' },
  { id: 15, name: 'TagBox' },
  { id: 16, name: 'TextArea' },
  { id: 17, name: 'TextBox' },
];

export const Cast = [
  { id: 1, name: 'UPPER' },
  { id: 2, name: 'LOWER' },
];

export const Tablas = [
  { id: 1, name: 'Ordenes' },
  { id: 2, name: 'Pacientes' },
  { id: 3, name: 'Estudios' },
];

export interface TypeCombobox {
  Id: number;
  Name: string;
  Filter: string;
  CampoId: string;
  CampoEstado: string;
  CampoFilter: string;
  CampoOrden: string;
  CampoVisible: string;
  Data: [any];
}

export interface ControlListasItem {
  label: string;
  value: string;
}

export enum GruposConfigGeneral {
  ConfiguracionGeneralDelSistema = 1,
  AdministracionDeCaches = 2,
  DisenoFormularios = 3,
  Ordenes = 4,
  Resultados = 5,
  ModulosDelSistema = 6,
  GeneralModulos = 7,
  Reportes = 8,
  TareasAutomaticas = 9,
}

export interface SysIAccionesMonitoreo {
  Acm_Id: number;
  Acm_Nombre: string;
  Acm_Descripcion?: string;
}
